'use client'
import { useEffect, useRef } from 'react'

const CSS = `
  .ff-wm {
    font-size: clamp(32px, 7vw, 88px);
    font-weight: 800;
    letter-spacing: -0.04em;
    color: #E8EEF8;
    white-space: nowrap;
    line-height: 1;
    position: relative;
    -webkit-font-smoothing: antialiased;
  }
  .ff-gap {
    display: inline-block;
    overflow: hidden;
    vertical-align: baseline;
    height: 1em;
  }
  .ff-airslot {
    display: inline-block;
    overflow: hidden;
    vertical-align: baseline;
    height: 1em;
    width: 0;
  }
  .ff-air {
    display: inline-block;
    vertical-align: baseline;
    transition: color .35s ease, text-shadow .35s ease;
  }
  .ff-air.ff-glow {
    color: #5B9FED;
    text-shadow: 0 0 28px rgba(91,159,237,.5);
  }
  .ff-plane {
    position: absolute;
    display: none;
    color: #5B9FED;
    font-size: 0.68em;
  }
  .ff-dot {
    display: inline-block;
    color: #5B9FED;
    overflow: hidden;
    vertical-align: baseline;
    width: 0;
    opacity: 0;
  }
  .ff-app {
    display: inline-block;
    color: #5B9FED;
    opacity: 0;
  }
`

export default function FairfaresWordmark() {
  const wmRef = useRef(null)

  useEffect(() => {
    const T = []
    const after = (ms, fn) => { const id = setTimeout(fn, ms); T.push(id); return id }
    let activePlaneAnim = null
    let initTimer
    const raf = requestAnimationFrame(() => {
      const wm = wmRef.current
      if (!wm) return

      const wPlane   = wm.querySelector('.ff-plane')
      const wFair    = wm.querySelector('.ff-fair')
      const wGap     = wm.querySelector('.ff-gap')
      const wAirSlot = wm.querySelector('.ff-airslot')
      const wAir     = wm.querySelector('.ff-air')
      const wDot     = wm.querySelector('.ff-dot')
      const wApp     = wm.querySelector('.ff-app')

      // ── One-time measurements ───────────────────────────────
      const spaceW = wGap.getBoundingClientRect().width
                    || parseFloat(getComputedStyle(wm).fontSize) * 0.27

      wPlane.style.cssText = 'position:absolute;top:-9999px;left:0;display:inline-block;font-size:.68em;color:#5B9FED'
      void wPlane.offsetWidth
      const planeW = wPlane.offsetWidth
      wPlane.style.cssText = 'position:absolute;display:none;color:#5B9FED;font-size:.68em'

      const GA = `display:inline-block;overflow:hidden;vertical-align:baseline;height:1em;width:${spaceW}px`
      wGap.style.cssText = GA
      void wm.offsetWidth

      const fairW      = wFair.offsetWidth
      const airTop     = wAir.offsetTop
      const airW       = wAir.offsetWidth
      const bigGap     = spaceW + airW
      const planePos   = fairW + spaceW
      const newAirLeft = fairW + bigGap

      const _t = Object.assign(document.createElement('span'), {
        style: 'position:absolute;visibility:hidden;font:inherit;letter-spacing:inherit',
        textContent: '.',
      })
      document.body.appendChild(_t)
      const dotW = _t.getBoundingClientRect().width
      document.body.removeChild(_t)

      const G0 = `display:inline-block;overflow:hidden;vertical-align:baseline;height:1em;width:0`
      const SA = `display:inline-block;overflow:hidden;vertical-align:baseline;height:1em;width:${airW}px`
      const S0 = `display:inline-block;overflow:hidden;vertical-align:baseline;height:1em;width:0`

      // ── Resets ──────────────────────────────────────────────
      function resetToA() {
        if (activePlaneAnim) { try { activePlaneAnim.cancel() } catch (e) {} activePlaneAnim = null }
        T.forEach(clearTimeout); T.length = 0
        wGap.style.cssText     = GA
        wAirSlot.style.cssText = S0
        wAir.style.cssText     = ''
        wAir.classList.remove('ff-glow')
        wPlane.style.cssText   = 'position:absolute;display:none;color:#5B9FED;font-size:.68em'
        wDot.style.cssText     = 'display:inline-block;overflow:hidden;vertical-align:baseline;color:#5B9FED;width:0;opacity:0'
        wApp.style.cssText     = 'display:inline-block;color:#5B9FED;opacity:0'
      }

      function resetToB() {
        if (activePlaneAnim) { try { activePlaneAnim.cancel() } catch (e) {} activePlaneAnim = null }
        T.forEach(clearTimeout); T.length = 0
        wGap.style.cssText     = G0
        wAirSlot.style.cssText = S0
        wAir.style.cssText     = 'display:none'
        wAir.classList.remove('ff-glow')
        wPlane.style.cssText   = 'position:absolute;display:none;color:#5B9FED;font-size:.68em'
        wDot.style.cssText     = `display:inline-block;overflow:hidden;vertical-align:baseline;color:#5B9FED;width:${dotW}px;opacity:1`
        wApp.style.cssText     = 'display:inline-block;color:#5B9FED;opacity:1'
      }

      // ── FORWARD: fair airfares → fairfares.app ──────────────
      function playForward() {
        resetToA()
        const dist    = window.innerWidth + 600
        const moveAmt = bigGap - spaceW   // = airW: plane + air travel together this far
        const dur     = 4200
        const t1      = 1200              // plane arrives at air
        const t2      = t1 + 900          // gap fully open, deposit

        wPlane.style.cssText = `position:absolute;left:${planePos}px;top:${airTop}px;display:inline-block;color:#5B9FED;font-size:.68em`

        activePlaneAnim = wPlane.animate([
          { transform: `translateX(${-dist}px)`,   filter: 'blur(4px)', easing: 'cubic-bezier(.15,0,.25,1)' },
          { transform: 'translateX(0)',             filter: 'blur(0)',   offset: t1 / dur, easing: 'linear' },
          { transform: `translateX(${moveAmt}px)`, filter: 'blur(0)',   offset: t2 / dur, easing: 'cubic-bezier(.55,0,1,1)' },
          { transform: `translateX(${dist}px)`,    filter: 'blur(5px)' },
        ], { duration: dur, fill: 'forwards' })

        // plane arrives at air → glow + gap opens synchronized
        after(t1, () => {
          wAir.classList.add('ff-glow')
          wGap.style.transition = `width ${t2 - t1}ms linear`
          wGap.style.width      = `${bigGap}px`
        })

        // gap fully open → air deposits, fares slides left → "fairfares"
        after(t2, () => {
          wAirSlot.style.cssText = SA
          wAir.style.cssText = `position:absolute;left:${newAirLeft}px;top:${airTop}px;display:inline-block;white-space:nowrap;color:#5B9FED;opacity:1`
          void wAir.offsetWidth
          const shut = `width 500ms cubic-bezier(.4,0,.2,1)`
          wGap.style.transition     = shut; wGap.style.width     = '0'
          wAirSlot.style.transition = shut; wAirSlot.style.width = '0'
          wAir.style.transition     = 'opacity 480ms ease'
          wAir.style.opacity        = '0'
        })

        // .app appears
        after(t2 + 560, () => {
          wDot.style.transition = `width 260ms cubic-bezier(.4,0,.2,1), opacity 220ms ease`
          wDot.style.width      = `${dotW}px`
          wDot.style.opacity    = '1'
          wApp.style.transition = 'opacity 340ms ease'
          wApp.style.opacity    = '1'
        })

        after(6500, playBackward)
      }

      // ── BACKWARD: fairfares.app → fair airfares ─────────────
      function playBackward() {
        resetToB()
        const dist  = window.innerWidth + 600
        const dur_b = 4000
        const t1_b  = 1300   // plane delivers air

        wPlane.style.cssText = `position:absolute;left:${planePos}px;top:${airTop}px;display:inline-block;color:#5B9FED;font-size:.68em`

        activePlaneAnim = wPlane.animate([
          { transform: `translateX(${-dist}px)`, filter: 'blur(4px)', easing: 'cubic-bezier(.15,0,.35,1)' },
          { transform: 'translateX(0)',           filter: 'blur(0)',   offset: t1_b / dur_b, easing: 'cubic-bezier(.55,0,1,1)' },
          { transform: `translateX(${dist}px)`,  filter: 'blur(5px)' },
        ], { duration: dur_b, fill: 'forwards' })

        // .app collapses
        after(150, () => {
          wDot.style.transition = 'width 360ms ease, opacity 280ms ease'
          wDot.style.width = '0'; wDot.style.opacity = '0'
          wApp.style.transition = 'opacity 360ms ease'; wApp.style.opacity = '0'
        })

        // plane delivers air: gap + air slide in simultaneously → fares glides right
        after(t1_b, () => {
          wGap.style.transition = `width 600ms cubic-bezier(.4,0,.2,1)`
          wGap.style.width      = `${spaceW}px`

          wAir.style.cssText = `display:inline-block;overflow:hidden;vertical-align:baseline;height:1em;width:0;opacity:0;color:#5B9FED;text-shadow:0 0 28px rgba(91,159,237,.5)`
          void wAir.offsetWidth
          wAir.style.transition = `width 600ms cubic-bezier(.4,0,.2,1), opacity 280ms ease`
          wAir.style.width      = `${airW}px`
          wAir.style.opacity    = '1'
        })

        // normalize air (remove overflow:hidden, keep glow)
        after(t1_b + 660, () => {
          wAir.classList.add('ff-glow')
          wAir.style.cssText = ''
          wAir.classList.add('ff-glow')
        })

        // unglow
        after(t1_b + 1300, () => {
          wAir.classList.remove('ff-glow')
        })

        after(6500, playForward)
      }

      initTimer = setTimeout(playForward, 400)

    })

    return () => {
      cancelAnimationFrame(raf)
      if (initTimer) clearTimeout(initTimer)
      T.forEach(clearTimeout)
      if (activePlaneAnim) { try { activePlaneAnim.cancel() } catch (e) {} }
    }
  }, [])

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: CSS }} />
      <div ref={wmRef} className="ff-wm">
        {/* inline-block comments prevent whitespace between spans */}
        <span className="ff-plane">✈</span>{/*
        */}<span className="ff-fair">fair</span>{/*
        */}<span className="ff-gap"> </span>{/*
        */}<span className="ff-airslot" />{/*
        */}<span className="ff-air">air</span>{/*
        */}<span className="ff-fares">fares</span>{/*
        */}<span className="ff-dot">.</span>{/*
        */}<span className="ff-app">app</span>
      </div>
    </>
  )
}
